import { api } from './client'

/**
 * 담당 근무지 매핑 — **대리출근 차단의 핵심**.
 *
 * 직원마다 "이 태그판에서만 찍을 수 있다"를 정한다.
 * 매핑이 **비어 있으면 조직 공용**(모든 태그판 허용)이고, 하나라도 있으면
 * 그 목록에서만 태깅이 된다 (서버 `nfcCheckin` 이 whitelist 검증. 거절 시 `SPOT_NOT_ASSIGNED`).
 *
 * 그래서 "매핑을 다 지우면 잠긴다"가 아니라 **"다 지우면 풀린다"**이다.
 * 화면이 이걸 분명히 말하지 않으면 관리자가 반대로 이해한다.
 *
 * 전부 ADMIN 전용 (서버도 role 체크).
 */

export interface MappedEmployee {
  employeeId: string
  employeeName: string
  employeeNumber: string | null
  departmentName: string | null
  spots: { id: string; name: string; location: string | null }[]
}

/** 매핑이 있는 직원만 나온다 — 없는 직원은 "제한 없음"이라 목록에 안 뜬다 */
export function getNfcMappings() {
  return api<MappedEmployee[]>('/api/nfc-mappings')
}

/** 특정 태그판에 매핑된 직원 (역방향 — "이 자리는 누가 쓰나") */
export function getEmployeesBySpot(spotId: string) {
  return api<{
    employeeId: string
    employeeName: string
    employeeNumber: string | null
    departmentName: string | null
  }[]>(`/api/nfc-mappings/by-spot/${spotId}`)
}

/**
 * 한 직원의 담당 근무지를 **통째로 교체**한다.
 * 빈 배열을 보내면 매핑이 사라지고 = 모든 태그판 허용으로 돌아간다.
 */
export function setEmployeeSpots(employeeId: string, nfcSpotIds: string[]) {
  return api<{ count: number }>('/api/nfc-mappings/set', {
    method: 'POST',
    body: JSON.stringify({ employeeId, nfcSpotIds }),
  })
}

/** 한 자리만 더한다 (기존 매핑 유지) */
export function addEmployeeSpot(employeeId: string, nfcSpotId: string) {
  return api<unknown>('/api/nfc-mappings/add', {
    method: 'POST',
    body: JSON.stringify({ employeeId, nfcSpotId }),
  })
}

/** 한 자리만 뗀다. 마지막 하나를 떼면 그 직원은 제한이 풀린다 */
export function removeEmployeeSpot(employeeId: string, nfcSpotId: string) {
  return api<unknown>('/api/nfc-mappings', {
    method: 'DELETE',
    body: JSON.stringify({ employeeId, nfcSpotId }),
  })
}

export interface BulkUploadResult {
  /** 실제로 적용된 (직원, 자리) 쌍 수 */
  applied: number
  /** 매핑이 갈아치워진 직원 수 */
  affected: number
  failed: { row: number; employeeNumber: string; reason: string }[]
}

/**
 * 여러 직원을 한 번에.
 *
 * `spotIdentifiers` 는 **자리 코드**(`R5-1` 같은 location)나 자리 ID 를 받는다.
 * 이름은 안 받는다 — "라파엘관 5층" 처럼 이름이 겹치는 자리가 있어 조용히 엉뚱한 곳에
 * 붙던 사고가 있었다 (DCMC 2026-06-26).
 */
export function bulkUploadMappings(
  items: { employeeNumber: string; spotIdentifiers: string[] }[],
) {
  return api<BulkUploadResult>('/api/nfc-mappings/bulk-upload', {
    method: 'POST',
    body: JSON.stringify({ items }),
  })
}
